import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEditorDeploymentsV1DeploymentsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/editor-deployments',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/editor-deployments',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/editor-deployments',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/editor-deployments',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_editor-deployments_deployments';

/** Creates a new Deployment */
export function createDeployment(payload: object): RequestOptionsFactory<any> {
  function __createDeployment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.CreateDeployment',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployment.updatedDate' },
              { path: 'deployment.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDeployment;
}

/** Get a Deployment by id */
export function getDeployment(payload: object): RequestOptionsFactory<any> {
  function __getDeployment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'GET' as any,
      methodFqn: 'wix.editor.deployments.v1.DeploymentsService.GetDeployment',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/{deploymentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployment.updatedDate' },
              { path: 'deployment.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeployment;
}

/** Publish a Deployment */
export function publishDeployment(payload: object): RequestOptionsFactory<any> {
  function __publishDeployment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.PublishDeployment',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/{deploymentId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployment.updatedDate' },
              { path: 'deployment.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishDeployment;
}

/** Publish an RC Deployment */
export function publishRcDeployment(
  payload: object
): RequestOptionsFactory<any> {
  function __publishRcDeployment({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.PublishRCDeployment',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/rc/{deploymentId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployment.updatedDate' },
              { path: 'deployment.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishRcDeployment;
}

/** List Deployments */
export function listDeployments(payload: object): RequestOptionsFactory<any> {
  function __listDeployments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'GET' as any,
      methodFqn: 'wix.editor.deployments.v1.DeploymentsService.ListDeployments',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployments.updatedDate' },
              { path: 'deployments.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDeployments;
}

/** Query Deployments */
export function queryDeployments(payload: object): RequestOptionsFactory<any> {
  function __queryDeployments({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.QueryDeployments',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployments.updatedDate' },
              { path: 'deployments.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryDeployments;
}

/** The pipeline provider has to send this event when its execution has ended. */
export function reportDeploymentPipelineResult(
  payload: object
): RequestOptionsFactory<any> {
  function __reportDeploymentPipelineResult({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.ReportDeploymentPipelineResult',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/pipeline/{deploymentId}/report-result',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __reportDeploymentPipelineResult;
}

/** Get Deployment Pipeline Description */
export function getDeploymentPipelineDescription(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeploymentPipelineDescription({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'GET' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.GetDeploymentPipelineDescription',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/pipeline/{deploymentId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getDeploymentPipelineDescription;
}

/** Updates extended fields of a Deployment without incrementing revision */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.document_management.editor_deployments.v1.deployment',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.deployments.v1.DeploymentsService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      url: resolveWixEditorDeploymentsV1DeploymentsServiceUrl({
        protoPath: '/v1/deployments/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deployment.updatedDate' },
              { path: 'deployment.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}
